import {
  Box,
  Button,
  CardMedia,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Badge,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import HistoryIcon from '@mui/icons-material/History';
import { Link as RouterLink } from 'react-router-dom';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  ApplicationStatusBasedOnRole,
  APPLICATION_LISTING_STATUS,
  APPLICATION_STATUS,
  IndividualApplicationStatusBasedOnRole,
  POA_APPLICATION_STATUS,
  REJECTED_ROLES,
  USER_ROLES,
  APPLICATION_LISTING_CHECKER_STATUS,
  APMIFlow,
  paginateCursor,
  CursorDirection,
} from '../../utils/constant';
import {
  applicationDownloadStatusCheck,
  checkAmcApproverStatusIsPending,
  checkIfApplicationIsNonIndividual,
  getFirstHolderName,
} from '../../utils/utilityFunctions';
import { ApplicationProps, esignResponse } from '../../redux-store/types/api-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllApplications,
  getAllApplicationsWithCursor,
} from '../../redux-store/actions/application';
import { GetAllApplicantionsResponseBody } from '../../redux-store/types/application';
import { CommonLayoutForPOA, Footer } from '../commonComponents';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import { ColumnType, DataTable } from '../DataTable';
import { useDebounce } from '../../utils/useDebounce';
import { RootStateType } from '../../redux-store/reducers';
import CloseIcon from '@mui/icons-material/Close';
import { formatShortDateAndTime } from '../../utils/date';
import { ApplicationDownload, ApplicationRefs } from '../../redux-store/actions/onBoarding';
import { FilterDropdown, initialFilters } from '../investors/filters';
import {
  Distributor,
  getDistributorsList,
  getDistributorsListForFilters,
  getRMsList,
} from '../../redux-store/actions';
import { ApplicationFilterType, RMType } from '../../redux-store/types/api-types';
import { reportDownload } from '../Reports/report-utils';
import { ApplicationTimeline } from '../ApplicationDetails';
import {
  getAuthorizedSignatoriesConsentDetails,
  getConsentDetails,
} from '../dashboard/applicationslist';
import { AccessibleDialog } from '../../lib/AccessibleDialog';
const getTableHeader = (role: string): ColumnType[] => {
  return [
    {
      header: 'Investor Name',
      valueGetter: ({ applicants = [] }: ApplicationProps) => getFirstHolderName(applicants),
    },
    {
      header: 'Application ID',
      label: 'applicationNumber',
    },
    {
      header: 'Mode of Holding',
      renderCell: (row: ApplicationProps) => {
        return (
          <Typography
            sx={{
              textTransform: 'capitalize',
            }}>
            {(row.modeOfHolding === 'either or survivor' ? 'Joint' : row.modeOfHolding) || 'Single'}
          </Typography>
        );
      },
    },
    // {
    //   header: 'Commited Value',
    //   valueGetter: (row: ApplicationProps) => row.commitmentAmount || 'N/A',
    // },
    {
      header: 'Investor PAN',
      valueGetter: ({ applicants = [] }: ApplicationProps) => applicants[0]?.panNo || 'N/A',
    },
    {
      header: 'Disclosure of exclusion',
      label: 'excludedCompaniesCheck',
      renderCell: (row: ApplicationProps) => {
        return (
          <Typography
            sx={{
              textTransform: 'capitalize',
            }}>
            {row.excludedCompaniesCheck || 'N/A'}
          </Typography>
        );
      },
    },
    {
      header: 'Client Base Code',
      label: 'clientCode',
      valueGetter: (row: ApplicationProps) => row.clientCode || 'N/A',
    },
    {
      header: 'Distributor Name',
      label: 'viewDistributorRM',
      valueGetter: (row: ApplicationProps) =>
        row.distributor?.name || row.distributor?.arnCode || 'N/A',
    },
    {
      header: 'Approval Status',
      renderCell: (row: ApplicationProps) => {
        const applicationStatus = row.hasPOA
          ? checkAmcApproverStatusIsPending(row) && role === USER_ROLES.AMCAPPROVER
            ? APPLICATION_LISTING_CHECKER_STATUS.pending
            : ApplicationStatusBasedOnRole[role][row.status]
          : checkIfApplicationIsNonIndividual(row)
          ? checkAmcApproverStatusIsPending(row) && role === USER_ROLES.AMCAPPROVER
            ? APPLICATION_LISTING_CHECKER_STATUS.pending
            : ApplicationStatusBasedOnRole[role][row.status]
          : !checkIfApplicationIsNonIndividual(row) && role === USER_ROLES.AMCAPPROVER
          ? checkAmcApproverStatusIsPending(row)
            ? APPLICATION_LISTING_CHECKER_STATUS.pending
            : IndividualApplicationStatusBasedOnRole[role][row.status]
          : APPLICATION_LISTING_STATUS[row.status];
        return (
          <Typography
            className={applicationStatus}
            sx={{
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: 14,
              color: '#996E09 !important',
              '&.Draft': {
                color: '#2a6aa6 !important',
              },
              '&.Completed': {
                fontWeight: 600,
                color: '#4B8251 !important',
              },
              '&.Rejected': {
                color: '#BD2932 !important',
                fontWeight: 500,
              },
            }}
            component={RouterLink}
            to={`/application-details/${row.id}`}>
            {APMIFlow
              ? applicationStatus === 'Pending' &&
                !checkIfApplicationIsNonIndividual(row) &&
                role === USER_ROLES.AMCAPPROVER &&
                row.editedByPrimaryApplicant
                ? applicationStatus + ' PMS Review'
                : applicationStatus
              : applicationStatus}
          </Typography>
        );
      },
    },
  ];
};

export function ApplicationsListView({
  tableHeaders,
}: {
  tableHeaders: ColumnType[];
}): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [applicationsList, setApplicationsList] = useState<ApplicationProps[]>([]);
  const [applicationsListData, setApplicationsListData] = useState<ApplicationProps[]>([]);
  const [timeLineDialog, setTimeLineDialog] = useState(false);
  const [timelineApplicationId, setTimeLineAapplicationId] = useState('');
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const {
    page,
    search: storeSearch,
    status: storeStatus,
    applicationType: storeApplicationType,
    modeOfHolding: storeModeOfHolding,
    distributorId: storeDistributorId,
    isSTPApplicable: storeSTPApplicable,
    amcApproverStatus,
    kycModified: storeKycModified,
    sortDirection: storeSortDirection,
    rowsPerPage: storeRowsPerPage,
    nextCursor: storeNextCursor,
    prevCursor: storePrevCursor,
    cursorDirection: storeCursorDirection,
    hasNext: storeHasNext,
    hasPrev: storeHasPrev,
    currentCursor: storeCurrentCursor,
  } = useSelector((store: RootStateType) => store.paramsObj);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<boolean>(false);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [filters, setFilters] = useState<ApplicationFilterType>(
    initialFilters(
      role,
      APPLICATION_LISTING_CHECKER_STATUS[amcApproverStatus || ''] ===
        APPLICATION_LISTING_CHECKER_STATUS.pending
        ? storeStatus.includes('sent_to_amc_approver')
          ? storeStatus
          : storeStatus + ',sent_to_amc_approver'
        : storeStatus,
      storeApplicationType,
      storeModeOfHolding,
      storeDistributorId,
      storeSTPApplicable,
      storeKycModified
    )
  );
  const { applicationType, distributorId, status, modeOfHolding, isSTPApplicable, kycModified } =
    filters;
  const [noOfPages, setNoPages] = useState(1);
  const [currentPage, setPage] = useState(page);
  const [search, setSearch] = useState(storeSearch);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const debounceSearchString = useDebounce(search, 500);
  const [distributorLoading, setDistributorLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>(
    (storeSortDirection as 'ASC' | 'DESC') || 'DESC'
  );
  const [rowsPerPage, setRowsPerPage] = useState(storeRowsPerPage || 10);
  const [nextCursor, setNextCursor] = useState<string | null>(storeNextCursor || null);
  const [prevCursor, setPrevCursor] = useState<string | null>(storePrevCursor || null);
  const [currentCursor, setCurrentCursor] = useState<string | null>(storeCurrentCursor || null);
  const [cursorDirection, setCursorDirection] = useState<CursorDirection>(
    (storeCursorDirection as CursorDirection) || CursorDirection.NEXT
  );
  const [hasNext, setHasNext] = useState(storeHasNext || false);
  const [hasPrev, setHasPrev] = useState(storeHasPrev || false);
  useEffect(() => {
    setApplicationsListData(applicationsList);
  }, [applicationsList]);
  useEffect(() => {
    let componentIsAlive = true;
    setLoading(true);
    setApplicationsList([]);
    (async function () {
      try {
        let response;
        if (paginateCursor) {
          response = (await dispatch(
            getAllApplicationsWithCursor({
              search,
              applicationType: applicationType.toString(),
              distributorId: distributorId.toString(),
              status: status
                .filter(
                  (ele) =>
                    APPLICATION_LISTING_STATUS[ele] !==
                    APPLICATION_LISTING_STATUS.sent_to_amc_approver
                )
                .toString(),
              modeOfHolding: modeOfHolding.toString(),
              isSTPApplicable: isSTPApplicable.toString(),
              amcApproverStatus: status.includes('sent_to_amc_approver') ? 'pending' : '',
              kycModified,
              limit: rowsPerPage,
              order: sortDirection,
              ...(currentCursor
                ? {
                    cursor: currentCursor,
                    direction: cursorDirection,
                  }
                : {}),
            })
          )) as unknown as GetAllApplicantionsResponseBody;
        } else {
          response = (await dispatch(
            getAllApplications({
              page: currentPage,
              search,
              applicationType: applicationType.toString(),
              distributorId: distributorId.toString(),
              status: status
                .filter(
                  (ele) =>
                    APPLICATION_LISTING_STATUS[ele] !==
                    APPLICATION_LISTING_STATUS.sent_to_amc_approver
                )
                .toString(),
              modeOfHolding: modeOfHolding.toString(),
              isSTPApplicable: isSTPApplicable.toString(),
              amcApproverStatus: status.includes('sent_to_amc_approver') ? 'pending' : '',
              kycModified,
            })
          )) as unknown as GetAllApplicantionsResponseBody;
        }
        const { data: applications, pages, nextCursor: nextC, prevCursor: prevC } = response || {};
        if (!componentIsAlive) {
          return;
        }
        setApplicationsList(applications);
        setNoPages(pages);
        setPage(1);
        setNextCursor(nextC || '');
        setPrevCursor(prevC || '');
        setHasNext(response.hasNext || false);
        setHasPrev(response.hasPrev || false);
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [
    !paginateCursor ? currentPage : null,
    debounceSearchString,
    filters,
    rowsPerPage,
    sortDirection,
    currentCursor,
    cursorDirection,
  ]);

  // useEffect(() => {
  //   setPage(page);
  // }, [debounceSearchString]);

  const handleFiltersAnchorEl = async () => {
    try {
      setFiltersAnchorEl(true);
      setDistributorLoading(true);
      const distributorList = (await dispatch(
        getDistributorsListForFilters()
      )) as unknown as Distributor[];
      setDistributors(distributorList);
      setDistributorLoading(false);
    } catch (e) {
      console.error((e as Error).message);
      setDistributorLoading(false);
    }
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  const handleFiltersAnchorElClose = () => {
    setFiltersAnchorEl(false);
  };
  const filterOptions = (): JSX.Element => {
    const badgeCount = [
      'applicationType',
      'modeOfHolding',
      'status',
      'distributorId',
      'isSTPApplicable',
      'kycModified',
    ].filter((filterKey) => (filters[filterKey] as string[])?.length)?.length;
    return (
      <>
        <IconButton
          sx={{ p: 0.5 }}
          id="filter-icon-button"
          aria-controls="filter-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleFiltersAnchorEl}>
          <Badge badgeContent={badgeCount} color="info">
            <CardMedia component="img" image="/images/filter-icon.svg" alt="Filter Icon" />
          </Badge>
        </IconButton>{' '}
        {filtersAnchorEl && (
          <FilterDropdown
            filters={filters}
            onFiltersChange={(value) => {
              setPage(1);
              setFilters(value);
            }}
            anchorEl={filtersAnchorEl}
            handleClose={handleFiltersAnchorElClose}
            distributors={distributors}
            loader={distributorLoading}
          />
        )}
      </>
    );
  };
  const timeLineCell = {
    header: '',
    renderCell: (row: ApplicationProps) => {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
            }}>
            {[USER_ROLES.AMCAPPROVER].includes(role) && (
              <Tooltip title={'Activity Logs'}>
                <IconButton
                  sx={{
                    p: 0,
                    pr: 0.5,
                    ':hover': {
                      bgcolor: '#F4FCFC',
                    },
                    cursor: 'pointer',
                    pointerEvents: 'all',
                  }}
                  aria-label="History">
                  <HistoryIcon
                    // fontSize="inherit"
                    sx={{ color: 'primary.main' }}
                    onClick={() => {
                      setTimeLineAapplicationId(row.id as string);
                      setTimeLineDialog(true);
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </>
      );
    },
  };
  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };
  const handleCursorPageChange = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    // If user clicks next
    if (newPage > currentPage - 1 && nextCursor) {
      setCurrentCursor(nextCursor);
      setCursorDirection(CursorDirection.NEXT);
    }
    // If user clicks prev
    else if (newPage < currentPage - 1 && prevCursor) {
      setCurrentCursor(prevCursor);
      setCursorDirection(CursorDirection.PREV);
    }
    // Optionally update currentPage if you want to show the page number
  };
  return (
    <Box
      sx={{
        ml: { xs: 0 },
        p: { xs: 2, md: '50px' },
        borderRadius: '50px 0 0 0',
        bgcolor: { xs: '', md: 'rgba(238, 244, 251, 0.5)' },
        // minHeight: 'calc(100vh - 108px)',
      }}>
      <Box component="main">
        <Box>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 18,
              fontWeight: 500,
              mb: { xs: 2, md: 4 },
              mt: 10,
              p: { xs: '5px 15px', md: '' },
              borderRadius: '10px',
            }}>
            All Applications
          </Typography>
          <Grid
            container
            sx={{
              p: '20px 30px',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              bgcolor: 'common.white',
              mb: 1.5,
            }}
            rowGap={1.5}>
            <Grid item container sx={{ width: 'unset', alignItems: 'flex-end', gap: 2 }}>
              <Grid item>
                <FormControl variant="standard" sx={{ display: 'block' }}>
                  <InputLabel
                    htmlFor="input-with-icon-adornment"
                    sx={{ '&.Mui-focused': { color: 'text.primary' } }}>
                    Search Application
                  </InputLabel>
                  <Input
                    id="input-with-icon-adornment"
                    value={search}
                    onChange={({ target: { value } }) => {
                      setPage(1);
                      setCurrentCursor(null);
                      setSearch(value);
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton aria-label="Search">
                          <SearchSharpIcon sx={{ color: 'primary.main' }} />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid item sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              {/* <FilterMenu /> */}
              {filterOptions()}
              {paginateCursor && (
                <>
                  <Tooltip title={sortDirection === 'ASC' ? 'Sort Ascending' : 'Sort Descending'}>
                    <span>
                      <IconButton
                        onClick={() => setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                        sx={{
                          backgroundColor: '#2057A6',
                          borderRadius: '7px',
                          p: 1.4,
                          '&:hover': {
                            backgroundColor: '#174483',
                          },
                        }}
                        aria-label="Ascending">
                        <CardMedia
                          component="img"
                          src="/images/ascending-sort.svg"
                          alt={sortDirection === 'ASC' ? 'Sort Descending' : 'Sort Ascending'}
                          sx={{
                            width: '1.1rem',
                            filter: 'invert(1) brightness(2)',
                            transform:
                              sortDirection === 'ASC'
                                ? 'rotate(180deg) scaleX(-1)'
                                : 'rotate(0deg) scaleX(1)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
              <Grid item> </Grid>
            </Grid>
          </Grid>{' '}
          <DataTable
            isLoading={isLoading}
            tableHeader={[...tableHeaders, timeLineCell]}
            tableData={applicationsList}
            noOfPages={noOfPages}
            currentPage={currentPage}
            paginationAction={handlePageChange}
            rowCustomStyles={{
              '&:last-child td, &:last-child th': { border: 0 },
              '.MuiTableCell-body ': { color: '#686e72' },
              background: 'transparent',
            }}
            handleCursorPageChange={handleCursorPageChange}
            hasNext={hasNext}
            hasPrev={hasPrev}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPage={rowsPerPage}
          />
          {timeLineDialog && (
            <ApplicationTimeline
              id={timelineApplicationId}
              dialogOpen={timeLineDialog}
              setDialogOpen={setTimeLineDialog}
              consentDetails={
                checkIfApplicationIsNonIndividual(
                  applicationsListData.find(
                    (application) => Number(application.id) === Number(timelineApplicationId)
                  ) as ApplicationProps
                )
                  ? getAuthorizedSignatoriesConsentDetails(
                      (
                        applicationsListData.find(
                          (application) => Number(application.id) === Number(timelineApplicationId)
                        ) as ApplicationProps
                      ).groups
                    )
                  : getConsentDetails(
                      (
                        applicationsListData.find(
                          (application) => Number(application.id) === Number(timelineApplicationId)
                        ) as ApplicationProps
                      ).applicants
                    )
              }
              application={
                applicationsListData.find(
                  (application) => Number(application.id) === Number(timelineApplicationId)
                ) as ApplicationProps
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function ApplicationEsignList(): JSX.Element {
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [rejectedApplication, setRejectedApplication] = useState<ApplicationProps>();
  const [checkPoaOrNI, setCheckPoaOrNI] = useState(false);
  const [isDialogLoading, setDialogLoading] = useState(false);
  const [id, setId] = useState('');
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [applicationRefsList, setApplicationRefsList] = useState<esignResponse | null>(null);
  const [tooltip, setTooltip] = useState('Click to Copy');

  const handleClose = () => {
    setOpen(false), setId('');
  };
  const handleDialogClose = () => {
    setDialogLoading(false);
    setOpen(true);
  };

  const handleRejectionDialogClose = () => {
    setRejectionDialog(false);
  };
  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        if (id) {
          const responseApplicationRefsList = (await dispatch(
            ApplicationRefs(id)
          )) as unknown as esignResponse;
          if (!componentIsAlive) {
            return;
          }
          setApplicationRefsList(responseApplicationRefsList);
          handleDialogClose();
        }
      } catch (e) {
        setDialogLoading(false);
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [id]);
  const rejectionCell = {
    header: '',
    renderCell: (row: ApplicationProps) => {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
            }}>
            {APPLICATION_STATUS[row.status] === 'Rejected' && (
              <IconButton aria-label="Comment">
                <CardMedia
                  component="img"
                  src="/images/comment.svg"
                  alt="comment"
                  sx={{ width: '20px' }}
                  onClick={() => {
                    setRejectedApplication(row);
                    setRejectionDialog(true);
                  }}
                />
              </IconButton>
            )}
          </Box>
        </>
      );
    },
  };
  const RefCell = {
    header: '',
    renderCell: (row: ApplicationProps) => {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
            }}>
            {USER_ROLES.AMCAPPROVER === role && (
              <>
                {!['draft', 'rejected', 'sent_to_poa_approver', 'sent_to_amc_approver'].includes(
                  row.status
                ) && (
                  <Tooltip title="Link">
                    <IconButton
                      sx={{
                        p: 0,
                        ':hover': {
                          bgcolor: '#F4FCFC',
                        },
                      }}
                      onClick={() => {
                        setId(row.id as string),
                          setDialogLoading(true),
                          setCheckPoaOrNI(row.hasPOA);
                      }}
                      aria-label="Link">
                      <CardMedia
                        component="img"
                        src="/images/link-icon.svg"
                        alt="Link"
                        sx={{ width: '30px' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {(row?.signedLeegalityFile?.url || USER_ROLES.AMCAPPROVER === role) && (
                  <Tooltip title="Download">
                    <IconButton
                      aria-label="Download"
                      disabled={downloadingId === row?.id}
                      sx={{
                        p: 0,
                        pl: 0.5,
                        pr: 0.5,
                        ':hover': { bgcolor: '#F4FCFC' },
                        cursor: downloadingId === row?.id ? 'not-allowed' : 'pointer',
                      }}
                      onClick={async () => {
                        if (downloadingId === row?.id) return;
                        setDownloadingId(row?.id || null);
                        try {
                          if (applicationDownloadStatusCheck(row?.status || '')) {
                            const res = (await dispatch(ApplicationDownload(row?.id || ''))) as any;

                            reportDownload(res as Blob, `${row?.id}.pdf`);
                          } else if (row?.signedLeegalityFile?.url) {
                            window.open(row?.signedLeegalityFile?.url);
                          }
                        } catch (e) {
                          console.error((e as Error).message);
                        } finally {
                          setDownloadingId(null);
                        }
                      }}>
                      {downloadingId === row?.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <CardMedia component="img" src="/images/download-icon.png" alt="Download" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </>
      );
    },
  };
  return (
    <>
      <CommonLayoutForPOA />
      <ApplicationsListView tableHeaders={[...getTableHeader(role), rejectionCell, RefCell]} />
      <AccessibleDialog
        onClose={handleClose}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={4}
          pb={2}>
          <Typography sx={{ color: 'primary.main', fontSize: '20px', fontWeight: 500 }}>
            Get Link
          </Typography>
          <IconButton onClick={handleClose} aria-label="Close">
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Grid>
        {applicationRefsList?.esignsArray &&
          applicationRefsList?.esignsArray?.length > 0 &&
          applicationRefsList?.esignsArray.map((ref) => {
            return (
              <React.Fragment key={ref.id}>
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  px={4}
                  py={1}
                  sx={{
                    ':hover': {
                      bgcolor: '#F4FCFC',
                    },
                    ':last-of-type': {
                      mb: 5,
                    },
                  }}>
                  <Typography
                    sx={{
                      color: 'rgba(41, 49, 57, 0.7)',
                      fontSize: '18px',
                      fontWeight: 500,
                    }}>
                    Copy Link For {ref.name}
                  </Typography>
                  <Tooltip title={tooltip}>
                    <IconButton
                      aria-label="Link"
                      onMouseOver={() => {
                        setTooltip('Copy Link');
                      }}
                      onClick={() => {
                        setTooltip('Copied'),
                          navigator.clipboard.writeText(
                            USER_ROLES.AUTHORISEDSIGNATORY === ref.signatureBy
                              ? `${window.location.origin}/authorized-signatories-investment-details/${ref.referenceId}/strategy-details`
                              : checkPoaOrNI
                              ? ref.invitationUrl
                              : ref?.signatureBy &&
                                ('fundManager' === ref.signatureBy ||
                                  USER_ROLES.AMCAUTHORISEDSIGNATORY === ref.signatureBy)
                              ? ref.invitationUrl
                              : `${window.location.origin}/investment-details/${ref.referenceId}/details`
                          );
                      }}>
                      <CardMedia
                        component="img"
                        src="/images/link-icon.svg"
                        alt="Link"
                        sx={{ width: '35px' }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </React.Fragment>
            );
          })}
      </AccessibleDialog>
      <AccessibleDialog
        onClose={handleDialogClose}
        open={isDialogLoading}
        sx={{
          '.MuiPaper-root ': {
            p: 4,
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
            },
          }}>
          <Typography
            component="span"
            sx={{ color: 'primary.main', lineHeight: '35px', textAlign: 'center' }}>
            Loading...
          </Typography>
        </Box>
      </AccessibleDialog>

      <AccessibleDialog
        onClose={handleRejectionDialogClose}
        open={rejectionDialog}
        sx={{
          '.MuiPaper-root ': {
            width: { xs: '70%', sm: '70%' },
          },
        }}>
        <IconButton
          onClick={handleRejectionDialogClose}
          sx={{ alignSelf: 'flex-end', mt: 2, mr: 2 }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        {rejectedApplication && (
          <Box
            sx={{
              width: { xs: '80%', sm: '80%' },
              mx: 'auto',
              '.MuiTypography-root': {
                fontWeight: 500,
              },
              mt: 1,
              mb: 5,
            }}>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 6.7 },
                mt: 1,
              }}>
              Application Id:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {rejectedApplication.applicationNumber}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 1.5 },
                mt: 1,
              }}>
              Application Status:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#BD2932',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {POA_APPLICATION_STATUS[rejectedApplication.status]}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 9 },
                mt: 1,
              }}>
              Rejected on:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#4879AA',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {formatShortDateAndTime(rejectedApplication.rejectedAt)}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                ml: { sm: 9.5 },
                mt: 1,
              }}>
              Rejected by:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {REJECTED_ROLES[rejectedApplication.rejectedByRole]}
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '18px',
                letterSpacing: '0.4px',
                mt: 1,
              }}>
              Reason for rejection:{' '}
              <Typography
                component="span"
                sx={{
                  color: '#337FC9',
                  fontSize: '16px',
                  letterSpacing: '0.4px',
                  ml: 0.5,
                }}>
                {rejectedApplication.rejectedReason}
              </Typography>
            </Typography>
            <Button
              variant="contained"
              sx={{ ml: '32%', lineHeight: 1.1, width: '30%', mt: 3 }}
              onClick={handleRejectionDialogClose}>
              Done
            </Button>
          </Box>
        )}
      </AccessibleDialog>
      <Footer />
    </>
  );
}
